import React from 'react'

export type ReceiptVaultDownloadLink = {
  label: string
  url: string
  fileType?: string
}

export type ReceiptVaultRuleObject = {
  text: string
  downloadLink?: ReceiptVaultDownloadLink
}

export type ReceiptVaultRule = string | ReceiptVaultRuleObject

export type ReceiptVaultRulesPayload = {
  title?: string
  version?: string
  intro?: string
  rules?: ReceiptVaultRule[]
}

export function isReceiptVaultRuleObject(rule: ReceiptVaultRule): rule is ReceiptVaultRuleObject {
  return typeof rule === 'object' && rule != null && typeof rule.text === 'string'
}

export function getReceiptVaultRuleText(rule: ReceiptVaultRule): string {
  if (typeof rule === 'string') return rule
  if (isReceiptVaultRuleObject(rule)) return rule.text
  return String(rule ?? '')
}

/** Plain-text line for tooltips (includes download label when present). */
export function formatReceiptVaultRuleForTooltip(rule: ReceiptVaultRule): string {
  const text = getReceiptVaultRuleText(rule)
  if (isReceiptVaultRuleObject(rule) && rule.downloadLink?.label) {
    return `${text} Download: ${rule.downloadLink.label}.`
  }
  return text
}

export function resolveReceiptVaultDownloadUrl(url: string): string {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url)) return url
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  const path = url.startsWith('/') ? url : `/${url}`
  return `${base}${path}`
}

type ReceiptVaultRuleItemProps = {
  rule: ReceiptVaultRule
  className?: string
}

export function ReceiptVaultRuleItem({ rule, className = '' }: ReceiptVaultRuleItemProps) {
  const text = getReceiptVaultRuleText(rule)
  const downloadLink = isReceiptVaultRuleObject(rule) ? rule.downloadLink : undefined

  return (
    <span className={className}>
      {text}
      {downloadLink?.url && downloadLink?.label ? (
        <>
          {' '}
          <a
            href={resolveReceiptVaultDownloadUrl(downloadLink.url)}
            className="ss-rv-rules-download-link fw-semibold"
            download
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download ${downloadLink.label}`}
          >
            {downloadLink.label}
          </a>
          {downloadLink.fileType ? ` (${downloadLink.fileType.toUpperCase()})` : null}
        </>
      ) : null}
    </span>
  )
}
